import { memo, useCallback, useState } from "react";
import { useTranslate } from "helpers";
import { ListingLocation } from "services/api/sales/listings/types";
import { AddressMap } from "ui/components/Map/AddressMap";
import { AddressData } from "ui/components/Map/AddressMap/types";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { useHandleDropdownChange, useHandleTextFieldChange } from "services/forms/hooks";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { EUROPEAN_COUNTRIES } from "services/api/settings";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";

interface ListingLocationEditCardProps {
  show: boolean;
  location: ListingLocation;
  onSuccess: (location: ListingLocation) => void;
  onClose: () => void;
}

const ListingLocationEditCard: React.FC<ListingLocationEditCardProps> = ({ show, location, onSuccess, onClose }) => {
  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<ListingLocation>({ ...location });
  const [mapPosition, setMapPosition] = useState<{ lat: number; lng: number } | undefined>(
    location.latitude && location.longitude
      ? { lat: location.latitude, lng: location.longitude }
      : undefined
  );

  const handleMapChange = useCallback((data: AddressData) => {
    setFormValues((prev) => ({
      ...prev,
      country: data.country || prev.country,
      city: data.city || prev.city,
      postalCode: data.postalCode || prev.postalCode,
      line1: data.line1 || prev.line1,
      latitude: data.latitude,
      longitude: data.longitude,
    }));

    if (data.latitude && data.longitude) {
      setMapPosition({ lat: data.latitude, lng: data.longitude });
    }
  }, []);

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);

  const handleSave = () => {
    onSuccess(formValues);
  };

  return (
    <Modal show={show} onHide={onClose} size="full">
      <ModalHeader
        title={t("admin.sales.listing.detail.location.edit")}
        icon="/media/images/add-icon.svg"
        onHide={onClose}
      />
      <ModalBody>
        <div className="flex h-[calc(100vh-8rem)] divide-x-2">
          <div className="flex-1">
            <AddressMap
              onChange={handleMapChange}
              initialPosition={mapPosition}
              formData={{
                country: formValues.country ?? undefined,
                city: formValues.city ?? undefined,
                line1: formValues.line1 ?? undefined,
                postalCode: formValues.postalCode ?? undefined,
              }}
            />
          </div>
          <div className="xl:w-1/4 lg:w-1/4 md:w-1/3 w-full p-4">
            <div className="flex flex-col h-full">
              <div className="flex-1 min-h-0">
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <Dropdown
                      name="country"
                      label={t("admin.contact.address.edit.country")}
                      placeholder={t("admin.contact.address.edit.country.placeholder")}
                      onChange={handleDropdownChange}
                      value={formValues.country}
                      options={EUROPEAN_COUNTRIES}
                    />
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="city"
                      label={t("admin.contact.address.edit.city")}
                      placeholder={t("admin.contact.address.edit.city.placeholder")}
                      onChange={handleTextFieldChange}
                      value={formValues.city}
                    />
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="postalCode"
                      label={t("admin.contact.address.edit.postalcode")}
                      placeholder={t("admin.contact.address.edit.postalcode.placeholder")}
                      onChange={handleTextFieldChange}
                      value={formValues.postalCode}
                    />
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="line1"
                      label={t("admin.contact.address.edit.line1")}
                      placeholder={t("admin.contact.address.edit.line1.placeholder")}
                      onChange={handleTextFieldChange}
                      value={formValues.line1}
                    />
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                <Button
                  variant="outlined"
                  children={t("admin.close")}
                  onClick={onClose}
                />
                <Button
                  variant="primary"
                  children={t("admin.save")}
                  onClick={handleSave}
                />
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default memo(ListingLocationEditCard);
